import arrayLength from "@-/com.npmjs.extra-array__length";
import { isVariationSelector } from "@-/dev.improve.haniverse.lib.unicode";

/**
 * The ONE font family the app ships. Every source face below (Noto Sans, Noto Sans SC/TC, Hanazono,
 * BabelStone, the IRG proposal faces, …) is subsetted to the glyphs the app uses and merged into
 * this family by `assets/fonts/han-composite`. Grapheme cards keep naming their SOURCE font — that
 * is build-time provenance deciding which face supplies each glyph's outline — but at runtime every
 * `<Text>` renders with this family.
 */
export const HANIVERSE_COMPOSITE_FONT_FAMILY = "Han Composite";

/**
 * The traditional-script delta face: only the codepoints whose Noto Sans TC outline differs from
 * the Noto Sans SC one that `Han Composite` carries. Chained to `Han Composite` on native and
 * listed before it on web, so anything the delta lacks falls back per glyph to the main face.
 */
export const HANIVERSE_COMPOSITE_TC_FONT_FAMILY = "Han Composite TC";

export type HanCompositeFontFamily =
  | typeof HANIVERSE_COMPOSITE_FONT_FAMILY
  | typeof HANIVERSE_COMPOSITE_TC_FONT_FAMILY;

/**
 * The code points the non-Regular `Han Composite` weight faces (Thin … Black) are GUARANTEED to
 * draw: printable ASCII, U+0020–U+007E inclusive. Those faces are deliberately compact Noto Sans
 * Latin subsets — ASCII plus whatever Latin-1 / pinyin / punctuation the UI happens to use — and
 * carry NO Han, kana, stroke, ideographic-description, private-use or astral glyphs; only the
 * Regular face does. The exact non-ASCII repertoire shifts with the UI copy, so nothing at runtime
 * relies on it; ASCII is the fixed core, and the font build's artifact test pins every Latin face
 * to contain all of it.
 *
 * Anything outside this range MUST be requested at weight 400 (see `graphemeFontWeight` in
 * `routes/nav/app/_lib/morpheme/grapheme/font`). Asking a Latin-only face for a glyph it lacks
 * leaves the outline to the platform's per-glyph fallback cascade (`expo-font-fallback` on
 * native), and that cascade has silently broken three times — a missing face in a stale build, an
 * absolute italic/condensed gate that rejected every CJK face, then a crash-vs-tofu oscillation in
 * the relative gate — each time turning every rare / Ext-B / Ext-G / private-use grapheme into a
 * LastResort box. Requesting the Regular face makes coverage a property of the font FILE the app
 * asks for, not a hope that the cascade is installed.
 */
export const HANIVERSE_COMPOSITE_LATIN_FACE_ASCII_RANGE = { max: 0x7e, min: 0x20 } as const;

/**
 * Whether every code point of `text` is printable ASCII, i.e. inside
 * {@link HANIVERSE_COMPOSITE_LATIN_FACE_ASCII_RANGE} — the only text the Latin-only `Han Composite`
 * weight faces are guaranteed to draw on their own. Empty text is vacuously drawable.
 */
export function isHanCompositeLatinFaceText(text: string): boolean {
  for (const codePointString of text) {
    const codePoint = codePointString.codePointAt(0);
    if (
      codePoint === undefined ||
      codePoint < HANIVERSE_COMPOSITE_LATIN_FACE_ASCII_RANGE.min ||
      codePoint > HANIVERSE_COMPOSITE_LATIN_FACE_ASCII_RANGE.max
    ) {
      return false;
    }
  }
  return true;
}

/** Fallback order when resolving which extension font owns a character. */
export const EXTENSION_FONT_FAMILY_PRIORITY_LIST = [
  // Stick is a single-glyph face carrying only the Katakana ﾊ (U+FF8A); it's first so ﾊ
  // always resolves to Stick before any CJK-coverage font is consulted.
  "Stick",
  // Nanum Square is a single-glyph face carrying only the Katakana コ (U+30B3); same card-pinned
  // pattern as Stick — its source cmap covers Hangul/CJK but only コ is ever taken from it.
  "Nanum Square",
  // Iansui is a single-glyph face carrying only the CJK radical ⺕ (U+2E95); card-pinned only —
  // its source covers traditional Chinese but only ⺕ is ever taken from it.
  "Iansui",
  // IRG N2878's CJKComp face provides source PUA forms for proposed component glyphs.
  "IRG N2878 CJKComp",
  // The GCP face from IRG N2878R3 carries the proposed component glyphs used by the app.
  "IRG N2878R3 GCP",
  // The UCP face carries UTC-03248 at the proposed U+2FBED position.
  "IRG N2878R3 UCP",
  // Academia Sinica's CDP EUDC face carries PUA component assignments such as U+EF3A.
  "Academia Sinica CDP EUDC Ming",
  // CNS plane-15 components (U+F616B full 朿, U+F616C left 朿); explicitly authored only.
  "TW-Kai-Plus",
  "Hanazono Mincho A Regular",
  "Hanazono Mincho B Regular",
  "BabelStone Han",
  "BabelStone Han PUA Custom",
  // Yuniversus carries Chinese component forms in the BMP PUA (e.g. U+F685, the interior of 四
  // without the outer 囗; U+F510). Card-pinned only — it is excluded from
  // `COVERAGE_ASSIGNED_FONT_FAMILY_LIST` for the same reason as the CDP EUDC and IRG faces.
  "Yuniversus",
  // Jigmo / Jigmo3 (KAMICHI Koichi, CC0) cover the recent CJK extensions that the bundled
  // BabelStone Han 15.0.4 and Hanazono predate — notably Ext J (Unicode 17), where U+327C1 𲟁 and
  // U+33473 𳑳 live, and the Unicode 15.1 stroke U+31E4 ㇤. The family splits its repertoire across
  // files by code point, so both are registered: `Jigmo` holds the BMP + early extensions, `Jigmo3`
  // the high planes. Card-pinned ONLY — see `COVERAGE_ASSIGNED_FONT_FAMILY_LIST`: Jigmo's cmap also
  // covers everyday Han (度, 人, 口), so letting it into the coverage cascade would take those
  // glyphs away from Noto/Hanazono.
  "Jigmo",
  // `Jigmo2` is DUAL-ROLE: it is listed here because it also owns bare characters (U+2CEA2 𬺢 is
  // the only bundled outline), and in `VARIATION_SEQUENCE_FONT_FAMILY_LIST` because it is the only
  // face carrying a real UVS mapping the app uses (the IVS 𦰩󠄂). The two roles are subset by
  // different paths in the font build and never collide: a variation SEQUENCE is keyed by
  // base+selector, a bare character by its single code point.
  "Jigmo2",
  "Jigmo3",
] as const;

export type ExtensionFontFamily = (typeof EXTENSION_FONT_FAMILY_PRIORITY_LIST)[number];

// Families that render Unicode variation SEQUENCES (base codepoint + a variation selector), not
// bare single characters. These are authored on the card whose glyph IS the full sequence, and are
// resolved BEFORE the single-character cascade, so e.g. ㇆︁ (U+31C6 U+FE01) routes to CJK Strokes
// AppF while the bare ㇆ (U+31C6) still resolves via the cascade above. Jigmo2 likewise owns the
// exact IVS 𦰩󠄂 (U+26C29 U+E0102), while bare 𦰩 keeps its independently authored decomposition and
// normal extension-font mapping.
//
// `Jigmo2` appears in `EXTENSION_FONT_FAMILY_PRIORITY_LIST` as well — it is dual-role, owning bare
// characters (U+2CEA2) in addition to that IVS. Membership here governs only how a SEQUENCE is
// resolved and subset, so the two roles are independent.
export const VARIATION_SEQUENCE_FONT_FAMILY_LIST = ["CJK Strokes AppF", "Jigmo2"] as const;

export type VariationSequenceFontFamily = (typeof VARIATION_SEQUENCE_FONT_FAMILY_LIST)[number];

/** Fallback order when subsetting fonts (includes Noto Sans SC/TC). */
export const FONT_FAMILY_PRIORITY_LIST = [
  "Noto Sans SC",
  "Noto Sans TC",
  ...EXTENSION_FONT_FAMILY_PRIORITY_LIST,
] as const;

export type SupportedFontFamily = (typeof FONT_FAMILY_PRIORITY_LIST)[number];

/** Every family a card may name in its `font` frontmatter. */
export type GraphemeFontFamily = SupportedFontFamily | VariationSequenceFontFamily;

/**
 * The TOML `font` value written for a glyph NO bundled font draws. It is deliberately not a family
 * name: such a glyph must resolve to `null` (so the renderer can avoid drawing tofu) rather than
 * silently falling back to Noto, which would claim coverage the bundle does not have.
 */
export const UNMAPPED_FONT_SENTINEL = "unmapped";

const SUPPORTED_FONT_FAMILY_SET = new Set<string>([
  ...FONT_FAMILY_PRIORITY_LIST,
  ...VARIATION_SEQUENCE_FONT_FAMILY_LIST,
]);

export function isGraphemeFontFamily(fontFamily: string): fontFamily is GraphemeFontFamily {
  return SUPPORTED_FONT_FAMILY_SET.has(fontFamily);
}

const EXTENSION_FONT_FAMILY_SET = new Set<string>(EXTENSION_FONT_FAMILY_PRIORITY_LIST);

export function isExtensionFontFamily(fontFamily: string): fontFamily is ExtensionFontFamily {
  return EXTENSION_FONT_FAMILY_SET.has(fontFamily);
}

const VARIATION_SEQUENCE_FONT_FAMILY_SET = new Set<string>(VARIATION_SEQUENCE_FONT_FAMILY_LIST);

export function isVariationSequenceFontFamily(
  fontFamily: string,
): fontFamily is VariationSequenceFontFamily {
  return VARIATION_SEQUENCE_FONT_FAMILY_SET.has(fontFamily);
}

// Known-wrong family names the card-authoring pass keeps emitting, mapped to the family that was
// unambiguously meant. "BabelStone Han PUA" (there IS no such family — the PUA face is "BabelStone
// Han PUA Custom") has now killed asset generation across every dev-CI job TWICE on two different
// freshly authored PUA graphemes (run 30946658148: U+F2CD; run 30976613375: U+F5FC). The intent is
// unambiguous, so normalize with a loud warning instead of dying — a typo'd study font must not
// take down every build. Anything NOT in this map still throws.
const FONT_FAMILY_ALIAS_MAP = new Map<string, GraphemeFontFamily>([
  ["BabelStone Han PUA", "BabelStone Han PUA Custom"],
]);

/**
 * The family a card's authored `font` string means, or `null` when it names nothing we bundle.
 * Applies the alias map first, so a known typo resolves (the caller is expected to warn).
 */
export function normalizeFontFamily(fontFamily: string): {
  aliased: boolean;
  fontFamily: GraphemeFontFamily | null;
} {
  const trimmed = fontFamily.trim();
  const aliasedFontFamily = FONT_FAMILY_ALIAS_MAP.get(trimmed);
  if (aliasedFontFamily !== undefined) {
    return { aliased: true, fontFamily: aliasedFontFamily };
  }
  return { aliased: false, fontFamily: isGraphemeFontFamily(trimmed) ? trimmed : null };
}

/**
 * The leading grapheme unit of `text`: a base code point plus any variation selector(s)
 * immediately following it (e.g. `"㇆︁…"` → `"㇆︁"`, `"㇆…"` → `"㇆"`). Used so a variation
 * sequence is matched as one key while a bare base character still resolves on its own.
 */
export function leadingGraphemeUnit(text: string): string {
  // Iterate by code point (string iteration yields whole code points) and keep the first one plus
  // any variation selector(s) directly after it. We stop at the first non-selector code point.
  let unit = "";
  let sawBase = false;
  for (const codePointString of text) {
    if (!sawBase) {
      unit = codePointString;
      sawBase = true;
      continue;
    }
    if (!isVariationSelector(codePointString)) {
      break;
    }
    unit += codePointString;
  }
  return sawBase ? unit : text;
}

/**
 * First private-use code point handed to an authored variation sequence, at the tail of Plane 16.
 *
 * `Han Composite` carries NO cmap format-14 (UVS) subtable — `fonteditor-core`, which writes the
 * merged font, drops that table — so a variation sequence (base + selector, e.g. the left-falling
 * dot `㇔︀` = U+31D4 U+FE00, or the Jigmo2 IVS `𦰩󠄂` = U+26C29 U+E0102) cannot be selected by the
 * platform shaper. Instead the font build homes each authored sequence's variant glyph at a
 * private-use code point of its own, and `variationSequenceDisplayText` rewrites the authored text
 * to that code point at render time. Selecting the font family alone is NOT sufficient.
 *
 * The allocation is a pure function of the sorted sequence list (see
 * {@link allocateVariationSequenceCodePoints}) so the build and the runtime agree without the app
 * ever importing a font-build artifact. The Plane-16 tail is used because it collides with nothing
 * any source face maps: the BMP private-use block carries the BabelStone / CJKComp / EUDC
 * component glyphs, Plane 15 carries CJK Strokes AppF's own variant scheme and Noto TC's PUA, and
 * Jigmo2's private-use mappings end well below U+10FF00.
 */
export const VARIATION_SEQUENCE_CODE_POINT_START = 0x10_ff_00;

/** Last private-use code point of Plane 16 (U+10FFFE and U+10FFFF are noncharacters). */
const VARIATION_SEQUENCE_CODE_POINT_END = 0x10_ff_fd;

/**
 * Deterministically assign a private-use code point to every authored variation sequence.
 *
 * `sequenceList` is sorted by code-unit order internally, so callers may pass the sequences in any
 * order and still get the same table — the font build and the runtime both call this over the
 * corpus projection's `sequences` keys. The result is the `sequence → code point` map used by both
 * the build (which homes the variant glyph there) and `variationSequenceDisplayText`.
 */
export function allocateVariationSequenceCodePoints(
  sequenceList: readonly string[],
): ReadonlyMap<string, number> {
  const sorted = [...new Set(sequenceList)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const capacity = VARIATION_SEQUENCE_CODE_POINT_END - VARIATION_SEQUENCE_CODE_POINT_START + 1;
  if (arrayLength(sorted) > capacity) {
    throw new Error(
      `[fonts] ${arrayLength(sorted)} variation sequences exceed the ${capacity} private-use code ` +
        "points reserved for them.",
    );
  }
  const allocation = new Map<string, number>();
  for (const [index, sequence] of sorted.entries()) {
    if (leadingGraphemeUnit(sequence) !== sequence || sequence.codePointAt(0) === undefined) {
      throw new Error(
        `[fonts] ${JSON.stringify(sequence)} is not a single base + variation selector sequence.`,
      );
    }
    allocation.set(sequence, VARIATION_SEQUENCE_CODE_POINT_START + index);
  }
  return allocation;
}

/**
 * Rewrites EVERY variation sequence in `text` that has an allocated private-use code point to that
 * code point, leaving all other code points untouched. Sequences absent from `sequenceCodePoints`
 * (nothing bundled draws them) pass through verbatim rather than becoming undrawable tofu.
 *
 * This must scan the whole string, not just the leading unit: a mnemonic decomposition is a full
 * IDS such as `⿱人㇔︀㇔︀`, where the sequences sit AFTER an ideographic-description character. Only
 * rewriting the leading unit left that string completely untouched (it starts with `⿱`), so both
 * dots kept rendering as the plain base `㇔`.
 */
export function variationSequenceDisplayText(
  text: string,
  sequenceCodePoints: ReadonlyMap<string, number>,
): string {
  // Code-point iteration is intentional: variation selectors are the unit we are pairing off, and
  // there are no emoji/ZWJ clusters in an IDS whose splitting would matter.
  // eslint-disable-next-line typescript/no-misused-spread -- code points are the unit we want
  const codePointList = [...text];
  let rewritten = "";
  for (let index = 0; index < arrayLength(codePointList); index += 1) {
    const codePointString = codePointList[index];
    if (codePointString === undefined) {
      continue;
    }
    const selectorString = codePointList[index + 1];
    const allocated =
      selectorString === undefined || !isVariationSelector(selectorString)
        ? undefined
        : sequenceCodePoints.get(codePointString + selectorString);
    if (allocated === undefined) {
      rewritten += codePointString;
      continue;
    }
    rewritten += String.fromCodePoint(allocated);
    // Skip the selector we just consumed.
    index += 1;
  }
  return rewritten;
}
